import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Search, X } from "lucide-react";
import {
  canOpenToolSearch, searchToolDirectory, SUGGESTED_TOOLS,
  TOOL_SEARCH_EXAMPLES, TOOL_SEARCH_SHORTCUT_KEY, TOOL_SECTION_LABELS,
} from "../lib/toolSearch";
import "../styles/tool-search.css";

const ToolSearchContext = createContext<{ openSearch: () => void; shortcutEnabled: boolean } | null>(null);

function readShortcutPreference() {
  try { return localStorage.getItem(TOOL_SEARCH_SHORTCUT_KEY) !== "off"; }
  catch { return true; }
}

/** One dialog/listener for the whole app shell, shared by header and tool navigation. */
export function ToolSearchProvider({ children, shortcutBlocked = false }: { children: ReactNode; shortcutBlocked?: boolean }) {
  const location = useLocation();
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const [shortcutEnabled, setShortcutEnabled] = useState(readShortcutPreference);
  const open = openedAt === location.key;
  const openSearch = useCallback(() => setOpenedAt(location.key), [location.key]);
  const closeSearch = useCallback(() => setOpenedAt(null), []);

  useEffect(() => {
    // An explicit launch link opens search; ordinary Back navigation stays closed.
    const requested = new URLSearchParams(location.search).get("tool-search") === "1";
    setOpenedAt(requested ? location.key : null);
  }, [location.key, location.search]);

  useEffect(() => {
    if (open || !shortcutEnabled || shortcutBlocked) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (!canOpenToolSearch(event)) return;
      event.preventDefault();
      openSearch();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, openSearch, shortcutEnabled, shortcutBlocked]);

  function updateShortcut(enabled: boolean) {
    setShortcutEnabled(enabled);
    try { localStorage.setItem(TOOL_SEARCH_SHORTCUT_KEY, enabled ? "on" : "off"); }
    catch { /* Honor the preference for this visit when storage is blocked. */ }
  }

  return <ToolSearchContext.Provider value={{ openSearch, shortcutEnabled }}>
    {children}
    {open && <ToolSearchDialog onDismiss={closeSearch} shortcutEnabled={shortcutEnabled} onShortcutChange={updateShortcut} />}
  </ToolSearchContext.Provider>;
}

export function ToolSwitcher({ header = false, onOpen }: { header?: boolean; onOpen?: () => void }) {
  const context = useContext(ToolSearchContext);
  if (!context) return null;
  return <button
    type="button"
    className={`fe-tool-help fe-tool-search-trigger${header ? " fe-tool-search-trigger-header" : ""}`}
    onClick={() => { onOpen?.(); context.openSearch(); }}
    aria-label="도구 찾기"
    aria-haspopup="dialog"
    aria-keyshortcuts={context.shortcutEnabled ? "f" : undefined}
    title={context.shortcutEnabled ? "도구 찾기 · 입력 중이 아닐 때 F" : "도구 찾기"}
  >
    <Search size={17} aria-hidden="true" />
    <span className="fe-tool-search-trigger-label">도구 찾기</span>
    {context.shortcutEnabled && <kbd aria-hidden="true">F</kbd>}
  </button>;
}

function ToolSearchDialog({ onDismiss, shortcutEnabled, onShortcutChange }: {
  onDismiss: () => void;
  shortcutEnabled: boolean;
  onShortcutChange: (enabled: boolean) => void;
}) {
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const composing = useRef(false);
  const backdropPointer = useRef(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const searching = Boolean(query.trim());
  const results = searching ? searchToolDirectory(query) : SUGGESTED_TOOLS;
  const activeTool = results[activeIndex];

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    dialog?.showModal();
    inputRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      dialog?.close();
      document.body.style.overflow = overflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  function changeQuery(value: string) {
    setQuery(value);
    setActiveIndex(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }

  function go(route: string) {
    onDismiss();
    navigate(route);
  }

  return <dialog
    ref={dialogRef}
    className="fe-tool-search-dialog"
    aria-labelledby="tool-search-title"
    aria-describedby="tool-search-description"
    onCancel={event => { event.preventDefault(); if (!composing.current) onDismiss(); }}
    onClose={event => { if (!event.currentTarget.open) onDismiss(); }}
    onKeyDown={event => {
      event.stopPropagation();
      if (event.key !== "Tab") return;
      const focusable = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled]):not([tabindex="-1"]), input:not([disabled])')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }}
    onPointerDown={event => { backdropPointer.current = event.target === event.currentTarget; }}
    onClick={event => { if (backdropPointer.current && event.target === event.currentTarget) onDismiss(); }}
  >
    <div className="fe-tool-search-heading">
      <div>
        <p className="fe-tool-search-eyebrow">도구 바로 찾기</p>
        <h2 id="tool-search-title">어떤 일을 하고 싶으세요?</h2>
        <p id="tool-search-description">도구 이름을 몰라도 괜찮아요. 하려는 일을 적어보세요.</p>
      </div>
      <button type="button" className="fe-tool-search-close" onClick={onDismiss} aria-label="도구 찾기 닫기"><X size={20} aria-hidden="true" /></button>
    </div>
    <div className="fe-tool-search-field">
      <Search size={21} aria-hidden="true" />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label="전체 도구 검색"
        aria-autocomplete="list"
        aria-expanded="true"
        aria-controls="tool-search-results"
        aria-activedescendant={activeTool ? `tool-search-option-${activeIndex}` : undefined}
        aria-describedby="tool-search-status"
        placeholder="예: 자리 바꾸기, 서명 받기, PDF 합치기"
        autoComplete="off"
        maxLength={120}
        spellCheck={false}
        enterKeyHint="go"
        value={query}
        onChange={event => changeQuery(event.target.value)}
        onCompositionStart={() => { composing.current = true; }}
        onCompositionEnd={() => { composing.current = false; }}
        onKeyDown={event => {
          if (composing.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
          if ((event.key === "ArrowDown" || event.key === "ArrowUp") && results.length) {
            event.preventDefault();
            const next = (activeIndex + (event.key === "ArrowDown" ? 1 : -1) + results.length) % results.length;
            setActiveIndex(next);
            document.getElementById(`tool-search-option-${next}`)?.scrollIntoView({ block: "nearest" });
          }
          if (event.key === "Enter") {
            event.preventDefault();
            if (activeTool) go(activeTool.route);
          }
        }}
      />
      {query && <button type="button" onClick={() => { changeQuery(""); inputRef.current?.focus(); }} aria-label="검색어 지우기"><X size={17} aria-hidden="true" /></button>}
    </div>
    {!searching && <div className="fe-tool-search-examples" aria-label="이렇게 검색해 보세요">
      {TOOL_SEARCH_EXAMPLES.map(example => <button key={example} type="button" onClick={() => { changeQuery(example); inputRef.current?.focus(); }}>{example}</button>)}
    </div>}
    <p id="tool-search-status" role="status" className="fe-tool-search-status">
      {searching ? `관련 도구 ${results.length}개` : "이런 도구가 있어요"}
    </p>
    <div ref={listRef} className="fe-tool-search-results" id="tool-search-results" role="listbox" aria-label="도구 검색 결과">
      {results.map((tool, index) => <button
        key={tool.route}
        id={`tool-search-option-${index}`}
        type="button"
        role="option"
        aria-selected={index === activeIndex}
        tabIndex={-1}
        className="fe-tool-search-result"
        onPointerMove={event => { if (event.pointerType === "mouse") setActiveIndex(index); }}
        onClick={() => go(tool.route)}
      >
        <span className="fe-tool-search-result-content">
          <span className="fe-tool-search-result-title"><strong>{tool.name}</strong><span>{TOOL_SECTION_LABELS[tool.section]}</span></span>
          <span className="fe-tool-search-summary">{tool.summary}</span>
        </span>
        <ArrowUpRight size={18} aria-hidden="true" />
      </button>)}
    </div>
    {results.length === 0 && <div className="fe-tool-search-empty">
      <p>아직 맞는 도구를 찾지 못했어요.</p>
      <span>‘출결’, ‘사진 모으기’, ‘시간 재기’처럼 바꿔보세요.</span>
      <button type="button" onClick={() => { changeQuery(""); inputRef.current?.focus(); }}>검색 초기화</button>
    </div>}
    <footer className="fe-tool-search-footer">
      <span className="fe-tool-search-keyboard-help"><kbd>↑</kbd><kbd>↓</kbd> 선택 <kbd>Enter</kbd> 이동 <kbd>Esc</kbd> 닫기</span>
      <label title="입력 중에는 작동하지 않아요"><input type="checkbox" checked={shortcutEnabled} onChange={event => onShortcutChange(event.target.checked)} /> F 단축키 사용</label>
    </footer>
  </dialog>;
}
